export interface Viewport {
	x: number
	y: number
	k: number
}

export interface Point {
	x: number
	y: number
}

export interface Size {
	w: number
	h: number
}

export interface Rect {
	minX: number
	minY: number
	maxX: number
	maxY: number
}

export interface Extent {
	left: number
	right: number
	top: number
	bottom: number
}

export interface PositionedNode {
	x?: number
	y?: number
}

export const MIN_K = 0.05
export const MAX_K = 4
export const FIT_PADDING = 48

/** Fitting never magnifies: a lone node stays at its natural size instead of filling the frame. */
export const MAX_FIT_K = 1

/** Content occupying less than this share of the frame on both axes is considered lost in empty space. */
const COVERAGE_THRESHOLD = 0.35

/** How far content may sit outside the visible rect before a refit is triggered, in screen pixels. */
const OUT_OF_VIEW_SLACK = 8

export const IDENTITY: Viewport = { x: 0, y: 0, k: 1 }

export function clamp (value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max)
}

export function clampScale (k: number) {
	return clamp(k, MIN_K, MAX_K)
}

export function screenToWorld (point: Point, viewport: Viewport): Point {
	return {
		x: (point.x - viewport.x) / viewport.k,
		y: (point.y - viewport.y) / viewport.k
	}
}

export function worldToScreen (point: Point, viewport: Viewport): Point {
	return {
		x: point.x * viewport.k + viewport.x,
		y: point.y * viewport.k + viewport.y
	}
}

export function isSameViewport (a: Viewport, b: Viewport, epsilon = 0.5) {
	return Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon && Math.abs(a.k - b.k) < epsilon / 1000
}

/**
 * Bounding box of the laid out content in world coordinates. `extentOf` reports how far a node
 * reaches beyond its centre on each side, which lets labels and icons be taken into account.
 */
export function contentBounds<T extends PositionedNode> (nodes: T[], extentOf?: (node: T) => Extent): Rect | null {
	let bounds: Rect | null = null
	for (const node of nodes) {
		const { x, y } = node
		if (!Number.isFinite(x) || !Number.isFinite(y)) continue
		const extent = extentOf ? extentOf(node) : { left: 0, right: 0, top: 0, bottom: 0 }
		const minX = (x as number) - extent.left
		const maxX = (x as number) + extent.right
		const minY = (y as number) - extent.top
		const maxY = (y as number) + extent.bottom
		if (!bounds) {
			bounds = { minX, minY, maxX, maxY }
		} else {
			if (minX < bounds.minX) bounds.minX = minX
			if (maxX > bounds.maxX) bounds.maxX = maxX
			if (minY < bounds.minY) bounds.minY = minY
			if (maxY > bounds.maxY) bounds.maxY = maxY
		}
	}
	return bounds
}

export function rectSize (rect: Rect): Size {
	return { w: rect.maxX - rect.minX, h: rect.maxY - rect.minY }
}

export function rectCenter (rect: Rect): Point {
	return { x: (rect.minX + rect.maxX) / 2, y: (rect.minY + rect.maxY) / 2 }
}

export function visibleWorldRect (viewport: Viewport, frameSize: Size): Rect {
	const topLeft = screenToWorld({ x: 0, y: 0 }, viewport)
	const bottomRight = screenToWorld({ x: frameSize.w, y: frameSize.h }, viewport)
	return { minX: topLeft.x, minY: topLeft.y, maxX: bottomRight.x, maxY: bottomRight.y }
}

/**
 * Viewport that centres `bounds` in the frame and scales it to fit, leaving `padding` screen pixels
 * around it. `minK` is a floor on the scale: past it the content is centred but left overflowing,
 * on the grounds that panning around a readable graph beats staring at an unreadable one.
 */
export function fitViewport (bounds: Rect, frameSize: Size, padding = FIT_PADDING, minK = MIN_K): Viewport {
	const { w, h } = rectSize(bounds)
	const availableW = Math.max(frameSize.w - 2 * padding, 1)
	const availableH = Math.max(frameSize.h - 2 * padding, 1)
	const scaleW = w > 0 ? availableW / w : MAX_FIT_K
	const scaleH = h > 0 ? availableH / h : MAX_FIT_K
	const floor = clamp(minK, MIN_K, MAX_FIT_K)
	const k = clamp(Math.max(Math.min(scaleW, scaleH, MAX_FIT_K), floor), MIN_K, MAX_K)
	const center = rectCenter(bounds)
	return {
		k,
		x: frameSize.w / 2 - center.x * k,
		y: frameSize.h / 2 - center.y * k
	}
}

/** Scales around `anchor` (screen coordinates) so the world point under the cursor stays put. */
export function zoomAt (viewport: Viewport, anchor: Point, factor: number): Viewport {
	const k = clampScale(viewport.k * factor)
	if (k === viewport.k) return viewport
	const world = screenToWorld(anchor, viewport)
	return {
		k,
		x: anchor.x - world.x * k,
		y: anchor.y - world.y * k
	}
}

export function panBy (viewport: Viewport, dx: number, dy: number): Viewport {
	return { ...viewport, x: viewport.x + dx, y: viewport.y + dy }
}

/** Viewport that keeps the current scale but puts `point` (world coordinates) in the middle of the frame. */
export function centerOn (point: Point, frameSize: Size, k: number): Viewport {
	return {
		k,
		x: frameSize.w / 2 - point.x * k,
		y: frameSize.h / 2 - point.y * k
	}
}

/**
 * The smart refit policy: refit when part of the graph has drifted out of sight, or when it has
 * shrunk into a small island of the frame. Otherwise the viewport the user chose is left alone.
 */
export function needsFit (bounds: Rect | null, viewport: Viewport, frameSize: Size): boolean {
	if (!bounds) return false
	const visible = visibleWorldRect(viewport, frameSize)
	const slack = OUT_OF_VIEW_SLACK / viewport.k
	const outOfView = bounds.minX < visible.minX - slack || bounds.maxX > visible.maxX + slack ||
		bounds.minY < visible.minY - slack || bounds.maxY > visible.maxY + slack
	if (outOfView) return true
	const { w, h } = rectSize(bounds)
	return (w * viewport.k) / frameSize.w < COVERAGE_THRESHOLD && (h * viewport.k) / frameSize.h < COVERAGE_THRESHOLD
}

/**
 * Interpolates between two viewports. Scale is eased geometrically so that zooming out and back in
 * feels linear to the eye, and the frame centre is carried along in world space to avoid the
 * sideways swoop a naive translation tween produces.
 */
export function interpolateViewport (from: Viewport, to: Viewport, t: number, frameSize: Size): Viewport {
	const k = from.k * Math.pow(to.k / from.k, t)
	const fromCenter = screenToWorld({ x: frameSize.w / 2, y: frameSize.h / 2 }, from)
	const toCenter = screenToWorld({ x: frameSize.w / 2, y: frameSize.h / 2 }, to)
	const center = {
		x: fromCenter.x + (toCenter.x - fromCenter.x) * t,
		y: fromCenter.y + (toCenter.y - fromCenter.y) * t
	}
	return centerOn(center, frameSize, k)
}

export function easeInOutCubic (t: number) {
	return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

export function prefersReducedMotion () {
	return typeof window !== 'undefined' && !!window.matchMedia &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
